'use client'

import { useCallback, useEffect, useState } from 'react'

import { classifyRateStructure } from '@/lib/z2h/rate-structure'

// Renders above the Z2H Agreements list: every pending counter-offer as a
// review card — original vs proposed side by side, the founder's note, and
// one-click Accept / Revise & send / Stand firm. The AI advisor runs ONLY
// when asked (button), and its suggestion can prefill the revise inputs.

const FIELDS = [
  ['upfrontAmount', 'Upfront', false],
  ['equityPct', 'Equity', true],
  ['paybackRatePct', 'Payback', true],
  ['growthRatePct', 'Growth', true],
  ['scalingRatePct', 'Scaling', true],
]

const usd = (n) => `$${Number(n || 0).toLocaleString()}`
const fmt = (value, pct) => (pct ? `${Number(value) || 0}%` : usd(value))

function CounterCard({ agreement, onDone }) {
  const t = agreement.terms || {}
  const c = agreement.counter || {}
  const changed = FIELDS.filter(([f]) => c[f] != null && Number(c[f]) !== Number(t[f]))

  // What the counter does to the deal's long-term shape — a counter that
  // turns a stepping-down rate into a flat/rising perpetuity changes
  // lifetime economics far more than the numbers alone suggest.
  const structureBefore = classifyRateStructure(t)
  const structureAfter = classifyRateStructure({
    ...t,
    ...Object.fromEntries(changed.map(([f]) => [f, c[f]])),
  })
  const structureChanged = structureAfter.kind !== structureBefore.kind

  const [mode, setMode] = useState(null) // null | 'revise' | 'stand-firm'
  const [revise, setRevise] = useState(() =>
    Object.fromEntries(changed.map(([f]) => [f, c[f]])),
  )
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(null) // 'accept' | 'revise' | 'stand-firm' | 'advise'
  const [advice, setAdvice] = useState(agreement.negotiationAdvice || null)
  const [error, setError] = useState(null)

  async function respond(action) {
    if (busy) return
    setBusy(action)
    setError(null)
    try {
      const res = await fetch(`/api/z2h/agreements/${agreement.id}/respond`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          ...(action === 'revise' ? { terms: revise } : {}),
          ...(message.trim() ? { message: message.trim() } : {}),
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      onDone(agreement.id, action)
    } catch (err) {
      setError(err.message)
      setBusy(null)
    }
  }

  async function askAdvisor() {
    if (busy) return
    setBusy('advise')
    setError(null)
    try {
      const res = await fetch(`/api/z2h/agreements/${agreement.id}/advise`, {
        method: 'POST',
        credentials: 'include',
      })
      const data = await res.json()
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setAdvice(data.advice)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  const btn = (primary) => ({
    padding: '8px 16px',
    borderRadius: 999,
    border: primary ? 'none' : '1px solid var(--theme-elevation-200)',
    background: primary ? '#C9A84C' : 'transparent',
    color: primary ? '#171717' : 'var(--theme-elevation-700)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  })

  return (
    <div
      style={{
        border: '1px solid #C9A84C55',
        borderRadius: 12,
        padding: '16px 20px',
        marginTop: 12,
        background: 'var(--theme-elevation-0)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 14 }}>
          {agreement.founderName || 'Founder'} · {agreement.ventureName || `Agreement #${agreement.id}`}
        </strong>
        <span style={{ fontSize: 12, color: 'var(--theme-elevation-500)' }}>
          countered {agreement.counter?.at ? new Date(agreement.counter.at).toLocaleString() : ''}
        </span>
      </div>

      <table style={{ marginTop: 10, fontSize: 13, borderCollapse: 'collapse' }}>
        <tbody>
          {changed.map(([f, label, pct]) => (
            <tr key={f}>
              <td style={{ padding: '3px 18px 3px 0', color: 'var(--theme-elevation-500)' }}>{label}</td>
              <td style={{ padding: '3px 14px 3px 0', textDecoration: 'line-through', color: 'var(--theme-elevation-400)' }}>
                {fmt(t[f], pct)}
              </td>
              <td style={{ padding: '3px 0', fontWeight: 700 }}>{fmt(c[f], pct)}</td>
              {mode === 'revise' && (
                <td style={{ paddingLeft: 16 }}>
                  <input
                    type="number"
                    value={revise[f] ?? ''}
                    min={0}
                    max={pct ? 100 : undefined}
                    step={pct ? 0.5 : 500}
                    onChange={(e) => setRevise((r) => ({ ...r, [f]: e.target.value }))}
                    style={{
                      width: 96,
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: '1px solid var(--theme-elevation-200)',
                      background: 'var(--theme-input-bg)',
                      color: 'var(--theme-elevation-800)',
                      fontSize: 13,
                    }}
                    aria-label={`Revised ${label}`}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <p
        style={{
          margin: '10px 0 0',
          fontSize: 12.5,
          fontWeight: structureChanged ? 700 : 400,
          color: structureChanged
            ? structureAfter.tone === 'emerald'
              ? '#16a34a'
              : '#b45309'
            : 'var(--theme-elevation-500)',
        }}
      >
        Rate structure:{' '}
        {structureChanged
          ? `${structureBefore.headline} → ${structureAfter.headline}`
          : structureAfter.headline}
        {structureAfter.kind === 'flat-perpetuity' || structureAfter.kind === 'steps-up'
          ? ' (runs in perpetuity at or above the payback rate)'
          : ''}
      </p>

      {agreement.counter?.note ? (
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 13,
            fontStyle: 'italic',
            borderLeft: '3px solid #C9A84C',
            paddingLeft: 10,
            color: 'var(--theme-elevation-600)',
          }}
        >
          “{agreement.counter.note}”
        </p>
      ) : null}

      {advice && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 10,
            background: 'var(--theme-elevation-50)',
            fontSize: 13,
          }}
        >
          <strong>
            AI advisor: {String(advice.recommendation || '').replace('-', ' ')}
          </strong>
          <p style={{ margin: '6px 0 0', color: 'var(--theme-elevation-600)' }}>{advice.rationale}</p>
          {(advice.negotiationNotes || []).length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--theme-elevation-600)' }}>
              {advice.negotiationNotes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}
          {advice.suggestedTerms && Object.keys(advice.suggestedTerms).length > 0 && (
            <button
              type="button"
              style={{ ...btn(false), marginTop: 8, padding: '5px 12px', fontSize: 12 }}
              onClick={() => {
                setRevise((r) => ({ ...r, ...advice.suggestedTerms }))
                setMode('revise')
              }}
            >
              Use suggested numbers → revise
            </button>
          )}
        </div>
      )}

      {(mode === 'stand-firm' || mode === 'revise') && (
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          maxLength={1200}
          placeholder={
            mode === 'revise'
              ? 'Optional message to the founder — the thinking behind the revision…'
              : 'Optional message — why the original terms stand…'
          }
          style={{
            marginTop: 12,
            width: '100%',
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid var(--theme-elevation-200)',
            background: 'var(--theme-input-bg)',
            color: 'var(--theme-elevation-800)',
            fontSize: 13,
            resize: 'vertical',
          }}
        />
      )}

      {error && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#dc2626' }}>{error}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        {mode === null && (
          <>
            <button type="button" style={btn(true)} disabled={!!busy} onClick={() => respond('accept')}>
              {busy === 'accept' ? 'Accepting…' : 'Accept counter'}
            </button>
            <button type="button" style={btn(false)} disabled={!!busy} onClick={() => setMode('revise')}>
              Revise & send back
            </button>
            <button type="button" style={btn(false)} disabled={!!busy} onClick={() => setMode('stand-firm')}>
              Stand firm
            </button>
            <button type="button" style={btn(false)} disabled={!!busy} onClick={askAdvisor}>
              {busy === 'advise' ? 'Thinking…' : '✦ Ask AI advisor'}
            </button>
          </>
        )}
        {mode === 'revise' && (
          <>
            <button type="button" style={btn(true)} disabled={!!busy} onClick={() => respond('revise')}>
              {busy === 'revise' ? 'Sending…' : 'Send revised terms'}
            </button>
            <button type="button" style={btn(false)} disabled={!!busy} onClick={() => setMode(null)}>
              Back
            </button>
          </>
        )}
        {mode === 'stand-firm' && (
          <>
            <button type="button" style={btn(true)} disabled={!!busy} onClick={() => respond('stand-firm')}>
              {busy === 'stand-firm' ? 'Sending…' : 'Confirm — hold original terms'}
            </button>
            <button type="button" style={btn(false)} disabled={!!busy} onClick={() => setMode(null)}>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export function CounterReviewPanel() {
  const [pending, setPending] = useState(null)
  const [resolved, setResolved] = useState({})

  const load = useCallback(async () => {
    try {
      const res = await fetch(
        '/payload-api/z2h-agreements?where[status][equals]=countered&limit=20&depth=0',
        { credentials: 'include' },
      )
      const data = await res.json()
      setPending(Array.isArray(data.docs) ? data.docs : [])
    } catch {
      setPending([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (!pending || pending.length === 0) return null

  return (
    <div
      style={{
        border: '1px solid #C9A84C',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 24,
        background: 'var(--theme-elevation-50)',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15 }}>
        ⚖️ Counter-offers awaiting your response ({pending.length})
      </h3>
      <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--theme-elevation-500)' }}>
        Signing is locked on these offers until you respond. Accept applies the
        founder&apos;s numbers; revise sends yours back; stand firm re-opens the
        original for signature. Every response emails the founder.
      </p>
      {pending.map((a) =>
        resolved[a.id] ? (
          <p key={a.id} style={{ marginTop: 12, fontSize: 13, color: '#16a34a' }}>
            ✓ {a.founderName || `Agreement #${a.id}`} — response sent ({resolved[a.id]}).
          </p>
        ) : (
          <CounterCard
            key={a.id}
            agreement={a}
            onDone={(id, action) => setResolved((r) => ({ ...r, [id]: action }))}
          />
        ),
      )}
    </div>
  )
}
